import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useList } from "@/lib/hooks";
import { money } from "@/components/common";
import { Truck, MapPin, Broadcast, CurrencyDollar, MagnifyingGlass, Crosshair } from "@phosphor-icons/react";

const DARK = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const LABELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const US = [[24.5, -125], [49.5, -66.5]];
const STATUS_COLOR = { "In Transit": "#38bdf8", "Booked": "#a78bfa", "Dispatched": "#f59e0b", "At Pickup": "#f97316", "At Delivery": "#22c55e" };
const color = (s) => STATUS_COLOR[s] || "#60a5fa";
const RouteWave = ({ positions, c, on, events }) => (
  <>
    <Polyline positions={positions} interactive={false} pathOptions={{ color: c, weight: on ? 14 : 10, opacity: 0.18, lineCap: "round" }} className="route-glow" />
    <Polyline positions={positions} interactive={false} pathOptions={{ color: c, weight: on ? 3 : 1.6, opacity: on ? 0.9 : 0.45, lineCap: "round" }} />
    <Polyline positions={positions} interactive={false} pathOptions={{ color: "#ffffff", weight: on ? 4 : 3, opacity: 0.95, dashArray: "1 34", lineCap: "round" }} className="route-wave" />
    <Polyline positions={positions} interactive={false} pathOptions={{ color: c, weight: on ? 6 : 4.5, opacity: 0.85, dashArray: "1 34", lineCap: "round" }} className="route-wave route-wave-2" />
    <Polyline positions={positions} pathOptions={{ color: c, weight: 16, opacity: 0 }} className="route-hit" eventHandlers={events} />
  </>
);
const fmtEta = (e) => new Date(e).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function arc(a, b, n = 40) {
  const [y1, x1] = [a.lat, a.lon], [y2, x2] = [b.lat, b.lon];
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2, dx = x2 - x1, dy = y2 - y1;
  const cx = mx - dy * 0.18, cy = my + dx * 0.18;
  return Array.from({ length: n + 1 }, (_, i) => { const t = i / n; return [(1 - t) ** 2 * y1 + 2 * (1 - t) * t * cy + t * t * y2, (1 - t) ** 2 * x1 + 2 * (1 - t) * t * cx + t * t * x2]; });
}

function Fly({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyToBounds(target, { padding: [80, 80], maxZoom: 9, duration: 1.1 }); }, [target, map]);
  return null;
}

function Kpi({ icon: Icon, label, value, accent, delay }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-4 py-3 animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 flex items-center gap-1.5"><Icon size={13} style={{ color: accent }} />{label}</p>
      <p className="text-2xl font-heading font-extrabold text-white mt-0.5 tabular">{value}</p>
    </div>
  );
}

function LoadRow({ p, active, onPick, onHover }) {
  return (
    <button onClick={() => onPick(p)} onMouseEnter={() => onHover(p.id)} onMouseLeave={() => onHover(null)}
      className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors ${active ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"}`} data-testid={`map-load-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm text-white">{p.reference}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: color(p.status), background: `${color(p.status)}1f` }}>{p.status}</span>
      </div>
      <p className="text-xs text-white/60 mt-1 truncate">{p.origin} <span className="text-white/30">→</span> {p.destination}</p>
      <p className="text-xs text-white/40 truncate">{p.driver ? `${p.driver} · ` : ""}{p.customer} · <span className="text-emerald-400 font-semibold">{money(p.rate)}</span></p>
      {p.eta && <p className="text-[11px] text-sky-400 mt-0.5">ETA {fmtEta(p.eta)}</p>}
    </button>
  );
}

export default function LoadMap() {
  const { data: pins = [], isLoading } = useList("loads-map", "/loads/map", { refetchInterval: 30000 });
  const { data: trackers = [] } = useList("tracking", "/tracking", { refetchInterval: 15000 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [hover, setHover] = useState(null);
  const [target, setTarget] = useState(null);
  const live = trackers.filter((t) => t.status === "active" && t.last);
  const statuses = ["All", ...Array.from(new Set(pins.map((p) => p.status)))];
  const shown = useMemo(() => pins.filter((p) => (status === "All" || p.status === status) && (!q || `${p.reference} ${p.origin} ${p.destination} ${p.driver} ${p.customer}`.toLowerCase().includes(q.toLowerCase()))), [pins, q, status]);
  const pick = (p) => { const pts = [p.origin_coord, p.dest_coord].filter(Boolean).map((c) => [c.lat, c.lon]); setTarget(pts.length > 1 ? pts : [pts[0], pts[0]]); setHover(p.id); };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8 h-[calc(100vh-4rem)] relative bg-[#06070a] overflow-hidden" data-testid="fleet-map">
      <MapContainer bounds={US} zoomControl={false} className="absolute inset-0 z-0" style={{ background: "#06070a" }} attributionControl={false}>
        <TileLayer url={DARK} maxZoom={16} />
        <TileLayer url={LABELS} maxZoom={16} />
        <Fly target={target} />
        {shown.map((p) => {
          const o = p.origin_coord, d = p.dest_coord, on = hover === p.id, c = color(p.status);
          return (
            <React.Fragment key={p.id}>
              {o && d && <RouteWave positions={arc(o, d)} c={c} on={on} events={{ mouseover: () => setHover(p.id), mouseout: () => setHover(null), click: () => pick(p) }} />}
              {o && <CircleMarker center={[o.lat, o.lon]} radius={5} pathOptions={{ color: "#fff", weight: 1.5, fillColor: "#10b981", fillOpacity: 1 }}><Tooltip direction="top" className="fleet-tip">{p.reference} · Pickup · {p.origin}</Tooltip></CircleMarker>}
              {d && <CircleMarker center={[d.lat, d.lon]} radius={on ? 9 : 6} pathOptions={{ color: c, weight: 2, fillColor: c, fillOpacity: 0.9 }} eventHandlers={{ click: () => pick(p) }}>
                <Tooltip direction="top" className="fleet-tip">{p.reference} · {p.destination}{p.eta ? ` · ETA ${fmtEta(p.eta)}` : ""}</Tooltip></CircleMarker>}
            </React.Fragment>
          );
        })}
        {live.map((t) => (
          <React.Fragment key={t.id}>
            <CircleMarker center={[t.last.lat, t.last.lng]} radius={14} pathOptions={{ color: "#22c55e", weight: 1, fillColor: "#22c55e", fillOpacity: 0.15 }} className="pin-blink" />
            <CircleMarker center={[t.last.lat, t.last.lng]} radius={6} pathOptions={{ color: "#fff", weight: 2, fillColor: "#22c55e", fillOpacity: 1 }}>
              <Tooltip direction="top" className="fleet-tip" permanent={live.length <= 6}>{t.name || t.phone} · LIVE{t.last.address ? ` · ${t.last.address}` : ""}</Tooltip>
            </CircleMarker>
          </React.Fragment>
        ))}
      </MapContainer>

      <div className="absolute top-5 left-5 right-5 lg:right-[400px] z-[500] pointer-events-none">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="pointer-events-auto">
            <p className="text-[11px] uppercase tracking-[0.3em] text-sky-400 font-semibold">Live operations</p>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-white tracking-tight">Fleet Map</h1>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pointer-events-auto">
            <Kpi icon={Truck} label="Active loads" value={pins.length} accent="#60a5fa" delay={0} />
            <Kpi icon={MapPin} label="In transit" value={pins.filter((p) => p.status === "In Transit").length} accent="#38bdf8" delay={60} />
            <Kpi icon={Broadcast} label="Live trucks" value={live.length} accent="#22c55e" delay={120} />
            <Kpi icon={CurrencyDollar} label="On the road" value={money(pins.reduce((s, p) => s + (Number(p.rate) || 0), 0))} accent="#34d399" delay={180} />
          </div>
        </div>
      </div>

      <aside className="absolute top-5 bottom-5 right-5 w-[360px] z-[500] hidden lg:flex flex-col rounded-3xl border border-white/10 bg-[#0b0d12]/80 backdrop-blur-2xl shadow-2xl overflow-hidden" data-testid="fleet-panel">
        <div className="p-4 border-b border-white/10 space-y-3">
          <div className="flex items-center gap-2 h-10 px-3 rounded-xl bg-white/[0.06] border border-white/10">
            <MagnifyingGlass size={16} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search load, city, driver…" className="bg-transparent outline-none text-sm text-white placeholder:text-white/30 flex-1" data-testid="fleet-search" />
          </div>
          <div className="flex flex-wrap gap-1.5">{statuses.map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${status === s ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:text-white"}`} data-testid={`fleet-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</button>))}</div>
        </div>
        {live.length > 0 && (
          <div className="border-b border-white/10 px-4 py-3" data-testid="fleet-live">
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-2 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live GPS</p>
            {live.map((t) => <button key={t.id} onClick={() => setTarget([[t.last.lat, t.last.lng], [t.last.lat, t.last.lng]])} className="w-full flex items-center justify-between text-left text-xs py-1 text-white/70 hover:text-white" data-testid={`fleet-live-${t.id}`}>
              <span className="truncate">{t.name || t.phone}</span><Crosshair size={14} className="text-emerald-400 shrink-0" /></button>)}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? <p className="p-4 text-sm text-white/40">Loading…</p> : shown.length === 0 ? (
            <div className="p-8 text-center"><MapPin size={28} className="mx-auto text-white/20" /><p className="text-sm text-white/60 mt-2 font-semibold">No active loads</p><p className="text-xs text-white/30 mt-1">Booked loads with recognizable cities appear here until delivered.</p></div>
          ) : shown.map((p) => <LoadRow key={p.id} p={p} active={hover === p.id} onPick={pick} onHover={setHover} />)}
        </div>
        <div className="px-4 py-2.5 border-t border-white/10 flex gap-4 text-[11px] text-white/40">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Pickup</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-400" />Delivery</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />Live truck</span>
        </div>
      </aside>
      <button onClick={() => setTarget([...US])} className="absolute bottom-5 left-5 z-[500] h-10 px-4 rounded-full bg-white/10 border border-white/15 backdrop-blur-xl text-white text-xs font-semibold hover:bg-white/20 transition-colors" data-testid="fleet-reset">Reset view</button>
    </div>
  );
}
